import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Package, Warehouse, ArrowLeftRight, ClipboardList,
  Trash2, ChefHat, Factory, Users, ShoppingCart, FileText, Receipt,
  LogOut, ChevronDown, ChevronRight, Building2, Menu, X, Repeat, Settings,
  CreditCard, TrendingUp, FileSpreadsheet, CalendarDays, Key, User,
  PanelLeftClose, PanelLeftOpen, Inbox, MapPin, HardHat, ClipboardCheck, MessageCircle,
  PackageCheck, CalendarRange, Network, ListChecks
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { isModuleEnabledFor } from '@/lib/modules';

// Nav sections with permission keys
const navSections = [
  {
    label: 'Ana Sayfa',
    permission: null, // Always visible
    items: [{ label: 'Kontrol Paneli', path: '/erp', icon: LayoutDashboard, permission: 'dashboard' }]
  },
  {
    label: 'Stok Yonetimi',
    permission: null,
    module: 'stok',
    items: [
      { label: 'Urun Kartlari', path: '/erp/products', icon: Package, permission: 'products' },
      { label: 'Depolar', path: '/erp/warehouses', icon: Warehouse, permission: 'warehouses' },
      { label: 'Stok Hareketleri', path: '/erp/stock/movements', icon: ArrowLeftRight, permission: 'stock_movements' },
      { label: 'Sayim', path: '/erp/stock/counts', icon: ClipboardList, permission: 'stock_counts' },
      { label: 'Fire / Zayi', path: '/erp/stock/waste', icon: Trash2, permission: 'waste' },
      { label: 'Siparis', path: '/erp/purchasing', icon: ShoppingCart, permission: 'purchasing' },
      { label: 'Mal Kabul', path: '/erp/mal-kabul', icon: PackageCheck, permission: 'stock_movements' },
    ]
  },
  {
    label: 'Uretim ve Satis',
    permission: null,
    module: 'uretim',
    items: [
      { label: 'Receteler', path: '/erp/recipes', icon: ChefHat, permission: 'recipes' },
      { label: 'Uretim ve Tuketim', path: '/erp/production-orders', icon: Factory, permission: 'production_orders' },
      // "Satislar" degil: bu ekran POS'tan gelen URUN SATIS HAREKETLERINI
      // listeliyor, bir satis BELGESI uretmiyor. ERP'de "Satis" denince fatura
      // anlasilir; asil satis modulu Satis Faturalari (kullanici, 2026-09-22).
      // Rota ve yetki anahtari `sales` olarak KALDI — degistirmek mevcut
      // kullanicilarin yetki matrisini sessizce bozardi.
      { label: 'Urun Satis Raporu', path: '/erp/sales', icon: TrendingUp, permission: 'sales' },
      { label: 'Satis Faturalari', path: '/erp/satis-faturalari', icon: Receipt, permission: 'satis_faturalari' },
    ]
  },
  {
    label: 'Muhasebe ve Finans',
    permission: null,
    module: 'muhasebe',
    items: [
      { label: 'Hesaplar ve Hareketler', path: '/erp/treasury', icon: Warehouse, permission: 'treasury' },
      { label: 'Cari Hesaplar', path: '/erp/suppliers', icon: Users, permission: 'suppliers' },
      { label: 'Alis Faturalari', path: '/erp/invoices', icon: Receipt, permission: 'invoices' },
      { label: 'Dosya Kutusu', path: '/erp/dosya-kutusu', icon: Inbox, permission: 'dosya_kutusu' },
      { label: 'Odeme Takvimi', path: '/erp/payment-calendar', icon: FileSpreadsheet, permission: 'payment_calendar' },
    ]
  },
  {
    label: 'Insan Kaynaklari',
    permission: null,
    module: 'personel',
    items: [
      { label: 'Personeller', path: '/erp/hr/employees', icon: Users, permission: 'employees' },
      { label: 'Puantaj', path: '/erp/hr/timesheet', icon: CalendarDays, permission: 'timesheet' },
      { label: 'İzinler', path: '/erp/hr/leaves', icon: CalendarRange, permission: 'employees' },
      { label: 'Organizasyon', path: '/erp/hr/organizasyon', icon: Network, permission: 'employees' },
    ]
  },
  {
    label: 'Insaat',
    permission: null,
    module: 'insaat',
    items: [
      { label: 'Santiyeler', path: '/erp/insaat/sites', icon: MapPin, permission: 'insaat_sites' },
      { label: 'Alt Yükleniciler', path: '/erp/insaat/subcontractors', icon: HardHat, permission: 'insaat_subcontractors' },
      { label: 'Gunluk Raporlar', path: '/erp/insaat/reports', icon: ClipboardCheck, permission: 'insaat_reports' },
      { label: 'İş Takibi', path: '/erp/insaat/isler', icon: ListChecks, permission: 'insaat_reports' },
    ]
  },
  {
    label: 'Ayarlar',
    permission: null,
    items: [
      { label: 'Admin Paneli', path: '/erp/admin', icon: Settings, permission: 'admin' },
      { label: 'WhatsApp Bildirimleri', path: '/erp/whatsapp', icon: MessageCircle, permission: 'whatsapp' },
      { label: 'POS Entegrasyonu', path: '/erp/pos-integration', icon: Receipt, permission: 'pos_integration', module: 'pos_integration' },
    ]
  }
];

export default function Layout({ children }) {
  const { user, company, companies, selectCompany, logout, hasPermission, changePassword } = useAuth();
  const [sirketDegisiyor, setSirketDegisiyor] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // All sections collapsed by default, except the one containing active path
  const [openSection, setOpenSection] = useState(() => {
    const active = navSections.find(s => s.label !== 'Ana Sayfa' && s.items.some(i => location.pathname.startsWith(i.path)));
    return active?.label || null;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  // Masaüstünde daraltılabilir menü — tercih oturumlar arası korunur
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('erp_sidebar_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('erp_sidebar_collapsed', collapsed ? '1' : '0'); } catch { /* yoksay */ }
  }, [collapsed]);

  // Statik rota → başlık eşlemesi
  useEffect(() => {
    const staticTitles = {
      '/erp': 'Kontrol Paneli',
      '/erp/products': 'Ürünler',
      '/erp/products/new': 'Yeni Ürün',
      '/erp/warehouses': 'Depolar',
      '/erp/stock/movements': 'Stok Hareketleri',
      '/erp/stock/counts': 'Sayımlar',
      '/erp/stock/counts/new': 'Yeni Sayım',
      '/erp/stock/waste': 'Fire Kayıtları',
      '/erp/recipes': 'Reçeteler',
      '/erp/recipes/new': 'Yeni Reçete',
      '/erp/production': 'Üretim Emirleri',
      '/erp/production/new': 'Yeni Üretim Emri',
      '/erp/production/consumption/new': 'Yeni Sarf',
      '/erp/suppliers': 'Cari Hesaplar',
      '/erp/suppliers/new': 'Yeni Cari',
      // NOT: eski yapida bunlar '/erp/purchasing/requests' gibi yollardaydi;
      // o rotalar yok, basliklar hicbir zaman eslesmiyordu.
      '/erp/purchasing': 'Sipariş',
      '/erp/purchase-requests/new': 'Yeni Talep',
      '/erp/purchase-orders': 'Satın Alma Siparişleri',
      '/erp/purchase-orders/new': 'Yeni Sipariş',
      '/erp/purchase-receipts/new': 'Mal Kabul',
      '/erp/mal-kabul': 'Mal Kabul',
      '/erp/mal-kabul/yeni': 'Yeni Mal Kabul',
      '/erp/transfers': 'Transferler',
      '/erp/invoices': 'Faturalar',
      '/erp/dosya-kutusu': 'Dosya Kutusu',
      '/erp/invoices/new': 'Yeni Fatura',
      '/erp/treasury': 'Hesaplar ve Hareketler',
      '/erp/treasury/payment-calendar': 'Ödeme Takvimi',
      '/erp/sales': 'Ürün Satış Raporu',
      '/erp/satis-faturalari': 'Satış Faturaları',
      '/erp/satis-faturalari/yeni': 'Yeni Satış Faturası',
      '/erp/hr/employees': 'Personel',
      '/erp/hr/employees/new': 'Yeni Personel',
      '/erp/hr/leaves': 'İzinler',
      '/erp/hr/organizasyon': 'Organizasyon',
      '/erp/hr/advances': 'Avans',
      '/erp/hr/payroll': 'Bordro',
      '/erp/hr/timesheet': 'Zaman Çizelgesi',
      '/erp/admin': 'Yönetim Paneli',
      '/erp/admin/pos': 'POS Entegrasyon',
      '/erp/admin/pos-transfer': 'POS Transfer',
      '/erp/expenses': 'Masraf Kalemleri',
      '/erp/barkod': 'Barkod',
      '/erp/insaat/sites': 'Şantiyeler',
      '/erp/insaat/subcontractors': 'Alt Yükleniciler',
      '/erp/insaat/reports': 'Günlük Raporlar',
      '/erp/insaat/isler': 'İş Takibi',
      '/erp/whatsapp': 'WhatsApp Bildirimleri',
    };
    const path = location.pathname;
    const exactTitle = staticTitles[path];
    if (exactTitle) {
      document.title = `TOROS ERP - ${exactTitle}`;
    } else if (!Object.keys(staticTitles).some(k => path.startsWith(k) && path !== k)) {
      // Bilinmeyen rota — sadece ana başlık
      document.title = 'TOROS ERP';
    }
    // Detail sayfaları kendi usePageTitle hook'larıyla override eder
  }, [location.pathname]);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const toggleSection = (label) => {
    setOpenSection(prev => prev === label ? null : label);
  };

  const isActive = (path) => {
    if (path === '/erp') return location.pathname === '/erp';
    return location.pathname.startsWith(path);
  };

  // Yetki kontrolü - sayfa veya bölüm görünür mü?
  const canView = (permission) => {
    if (!permission) return true; // null permission = always visible
    return hasPermission(permission, 'view');
  };

  // Filtrelenmiş nav sections
  const filteredSections = navSections.map(section => {
    // Section'ın görünür item'larını filtrele — hem izin hem de seçili
    // şirkette o modülün açık olması gerekiyor (bkz. lib/modules.js)
    const visibleItems = section.items.filter(item =>
      canView(item.permission) && isModuleEnabledFor(company, item.module || section.module));

    // En az bir görünür item varsa ve section yetkisi varsa göster
    if (visibleItems.length === 0) return null;
    if (section.permission && !canView(section.permission)) return null;

    return { ...section, items: visibleItems };
  }).filter(Boolean);

  const handleChangePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await changePassword(passwordForm.current, passwordForm.new);
      toast.success('Şifre başarıyla değiştirildi');
      setShowChangePassword(false);
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // ŞİRKET DEĞİŞİMİ TAM SAYFA YENİLEMEYLE yapılır, React yönlendirmesiyle değil.
  //
  // `selectCompany` yeni bir token alıp context'i günceller ama AÇIK SAYFALARIN
  // verisi eski şirkete ait kalır: sayfaların çoğu veriyi `useEffect(..., [])`
  // ile bir kez çekiyor, şirket değişimini dinlemiyor. Ekranda eski şirketin
  // ürünleri/faturaları dururken yeni şirkete kayıt açılabilirdi — sessiz ve
  // pahalı bir hata. Tam yenileme her sayfayı temiz başlatır ve bu sınıfın
  // tamamını kapatır; maliyeti bir sayfa yüklemesi.
  const sirketSec = async (companyId) => {
    if (!companyId || companyId === company?.id || sirketDegisiyor) return;
    setSirketDegisiyor(true);
    try {
      const r = await selectCompany(companyId);
      toast.success(`${r?.company?.name || 'Şirket'} seçildi`);
      window.location.href = '/erp';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Şirket değiştirilemedi');
      setSirketDegisiyor(false);
    }
  };

  // Daraltma yalnızca masaüstü içindir; mobil çekmece açıkken her zaman tam menü gösterilir
  const isCompact = collapsed && !mobileOpen;
  // Daraltılmış menüde ikonun yanında beliren etiket. Menü alanı taşmayı kırptığı için
  // (overflow-y-auto) etiket sabit konumla, menü dışında render edilir.
  const [hoveredNav, setHoveredNav] = useState(null);

  const sidebar = (
    <div className="flex flex-col h-full" data-testid="sidebar">
      {/* Logo */}
      <div className={`border-b border-[#d0c4b0] ${isCompact ? 'px-2 py-4' : 'px-5 py-5'}`}>
        <div className={`flex items-center ${isCompact ? 'justify-center' : 'justify-between'} gap-2`}>
          <Link to="/erp" className="flex items-center gap-2 min-w-0" data-testid="sidebar-logo"
            title={isCompact ? 'TorosERP' : undefined}>
            <div className="w-8 h-8 bg-[#2e2d29] rounded-md flex items-center justify-center flex-shrink-0">
              <span className="text-[#f5f0e8] font-extrabold text-sm">T</span>
            </div>
            {!isCompact && <span className="text-lg font-extrabold text-[#1a1916] tracking-tight truncate">TorosERP</span>}
          </Link>
          {!isCompact && (
            <button
              onClick={() => setCollapsed(true)}
              aria-label="Menüyü daralt"
              title="Menüyü daralt"
              className="hidden lg:flex h-8 w-8 items-center justify-center rounded-md text-[#8a7e70] hover:bg-[#2e2d29]/[0.06] hover:text-[#1a1916] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3d6e4e]"
              data-testid="sidebar-collapse">
              <PanelLeftClose size={16} strokeWidth={1.8} />
            </button>
          )}
        </div>
        {isCompact && (
          <button
            onClick={() => setCollapsed(false)}
            aria-label="Menüyü genişlet"
            title="Menüyü genişlet"
            className="hidden lg:flex mt-3 h-8 w-full items-center justify-center rounded-md text-[#8a7e70] hover:bg-[#2e2d29]/[0.06] hover:text-[#1a1916] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3d6e4e]"
            data-testid="sidebar-expand">
            <PanelLeftOpen size={16} strokeWidth={1.8} />
          </button>
        )}
        {company && !isCompact && (
          companies && companies.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  disabled={sirketDegisiyor}
                  title="Şirket değiştir"
                  className="mt-3 flex w-full items-center gap-2 rounded-md p-1 -ml-1 text-sm text-[#8a7e70] hover:bg-[#d0c4b0]/40 hover:text-[#1a1916] transition-colors disabled:opacity-60"
                  data-testid="sirket-degistir">
                  <Building2 size={14} strokeWidth={1.5} className="flex-shrink-0" />
                  <span className="truncate font-medium text-left">{company.name}</span>
                  <ChevronDown size={13} className="ml-auto flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {companies.map(c => (
                  <DropdownMenuItem key={c.id} onClick={() => sirketSec(c.id)}
                    disabled={c.id === company.id || sirketDegisiyor}
                    data-testid={`sirket-secenek-${c.id}`}>
                    <Building2 size={14} className="mr-2 flex-shrink-0" />
                    <span className="truncate">{c.name}</span>
                    {c.id === company.id && (
                      <span className="ml-auto text-xs text-[#3d6e4e] font-semibold">seçili</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="mt-3 flex items-center gap-2 text-sm text-[#8a7e70]">
              <Building2 size={14} strokeWidth={1.5} />
              <span className="truncate font-medium">{company.name}</span>
            </div>
          )
        )}
        {/* Daraltılmış menüde de şirket değiştirilebilmeli: tek ikon, aynı liste */}
        {company && isCompact && companies && companies.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={sirketDegisiyor}
                title={`${company.name} — şirket değiştir`}
                aria-label="Şirket değiştir"
                className="mt-3 flex h-8 w-full items-center justify-center rounded-md text-[#8a7e70] hover:bg-[#2e2d29]/[0.06] hover:text-[#1a1916] transition-colors disabled:opacity-60"
                data-testid="sirket-degistir-dar">
                <Building2 size={16} strokeWidth={1.8} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              {companies.map(c => (
                <DropdownMenuItem key={c.id} onClick={() => sirketSec(c.id)}
                  disabled={c.id === company.id || sirketDegisiyor}>
                  <Building2 size={14} className="mr-2 flex-shrink-0" />
                  <span className="truncate">{c.name}</span>
                  {c.id === company.id && (
                    <span className="ml-auto text-xs text-[#3d6e4e] font-semibold">seçili</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Şirket değişimi TAM SAYFA yenilemeyle yapılır (aşağıdaki nedenle). */}
      {/* Navigation */}
      <nav
        onScroll={() => hoveredNav && setHoveredNav(null)}
        className={`flex-1 overflow-y-auto overflow-x-hidden py-4 ${isCompact ? 'px-2' : 'px-3'}`}>
        {isCompact ? (
          <div>
            {filteredSections.map((section, si) => (
              <div key={section.label}>
                {/* Bölümler arası ayırıcı — daraltılmış menüde grupları görsel olarak ayırır */}
                {si > 0 && <div className="my-2 mx-1.5 border-t border-[#d0c4b0]" role="separator" aria-label={section.label} />}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        aria-label={item.label}
                        onMouseEnter={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          setHoveredNav({ label: item.label, top: r.top + r.height / 2 });
                        }}
                        onMouseLeave={() => setHoveredNav(null)}
                        onFocus={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          setHoveredNav({ label: item.label, top: r.top + r.height / 2 });
                        }}
                        onBlur={() => setHoveredNav(null)}
                        data-testid={`nav-item-${item.path}`}
                        className={`flex h-10 items-center justify-center rounded-md transition-colors duration-150
                          ${active
                            ? 'bg-[#2e2d29]/[0.10] text-[#1a1916] ring-1 ring-[#3d6e4e]/40'
                            : 'text-[#5a554e] hover:bg-[#2e2d29]/[0.06] hover:text-[#1a1916]'
                          }`}
                      >
                        <Icon size={18} strokeWidth={active ? 2 : 1.5} />
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          filteredSections.map((section) => (
            <div key={section.label} className="mb-3">
              {section.label !== 'Ana Sayfa' && (
                <button
                  onClick={() => toggleSection(section.label)}
                  className="flex items-center justify-between w-full px-2 py-1.5 text-[11px] font-semibold tracking-wide text-[#8a7e70] hover:text-[#3F3E39] transition-colors"
                  data-testid={`nav-section-${section.label}`}
                >
                  <span className="uppercase">{section.label}</span>
                  {openSection === section.label ? <ChevronDown size={12} strokeWidth={2.5} /> : <ChevronRight size={12} strokeWidth={2.5} />}
                </button>
              )}
              {(section.label === 'Ana Sayfa' || openSection === section.label) && (
                <div className="space-y-0.5 mt-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`nav-item-${item.path}`}
                        className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-150
                          ${active
                            ? 'bg-[#2e2d29]/[0.07] text-[#1a1916] font-semibold border-l-[2.5px] border-[#3d6e4e]'
                            : 'text-[#5a554e] hover:bg-[#2e2d29]/[0.05] hover:text-[#1a1916] border-l-[2.5px] border-transparent'
                          }`}
                      >
                        <Icon size={18} strokeWidth={1.5} />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </nav>

      {/* User */}
      <div className={`border-t border-[#d0c4b0] py-4 ${isCompact ? 'px-2' : 'px-4'}`}>
        <div className="flex items-center justify-between">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                title={isCompact ? (user?.name || 'Hesap') : undefined}
                aria-label={isCompact ? (user?.name || 'Hesap menüsü') : undefined}
                className={`flex items-center gap-2 min-w-0 hover:bg-[#d0c4b0]/40 rounded-md transition-colors ${isCompact ? 'w-full justify-center p-1.5' : 'p-2 -ml-2'}`}>
                <div className="w-8 h-8 bg-[#2e2d29] rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-[#f5f0e8]" />
                </div>
                {!isCompact && (
                  <div className="min-w-0 text-left">
                    <p className="text-sm font-semibold text-[#1a1916] truncate">{user?.name}</p>
                    <p className="text-xs text-[#8a7e70] truncate">{user?.email}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
                <Key size={14} className="mr-2" />
                Şifremi Değiştir
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut size={14} className="mr-2" />
                Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#e8dfd0]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#faf7f2] border-b border-[#d0c4b0] shadow-sm z-30 flex items-center px-4">
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} className="mr-3">
          <Menu size={20} />
        </Button>
        <span className="font-bold text-[#1a1916]">TorosERP</span>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — mobilde her zaman tam genişlik, masaüstünde daraltılabilir */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[#faf7f2] border-r border-[#d0c4b0] shadow-[1px_0_12px_rgba(46,45,41,0.06)] z-50
          w-64 ${collapsed ? 'lg:w-[68px]' : 'lg:w-64'}
          transition-[transform,width] duration-200 ease-out
          lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="lg:hidden absolute top-4 right-4">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
            <X size={20} />
          </Button>
        </div>
        {sidebar}
      </aside>

      {/* Daraltılmış menüde ikon etiketi — sidebar dışında, kırpılmasın diye sabit konumlu */}
      {isCompact && hoveredNav && (
        <div
          role="tooltip"
          className="hidden lg:block fixed z-[60] left-[74px] -translate-y-1/2 px-2.5 py-1.5 rounded-md
            bg-[#2e2d29] text-[#f5f0e8] text-xs font-medium whitespace-nowrap
            shadow-[0_4px_12px_rgba(26,25,22,0.25)] pointer-events-none"
          style={{ top: hoveredNav.top }}
        >
          {hoveredNav.label}
        </div>
      )}

      {/* Main content */}
      <main className={`pt-14 lg:pt-0 min-h-screen transition-[margin] duration-200 ease-out ${collapsed ? 'lg:ml-[68px]' : 'lg:ml-64'}`}>
        <div className="page-content">
          {children}
        </div>
      </main>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Şifre Değiştir</DialogTitle>
            <DialogDescription className="text-[#8a7e70]">
              Güvenliğiniz için şifrenizi düzenli olarak değiştirin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Mevcut Şifre</Label>
              <Input 
                type="password"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Yeni Şifre</Label>
              <Input 
                type="password"
                value={passwordForm.new}
                onChange={(e) => setPasswordForm(p => ({ ...p, new: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Yeni Şifre (Tekrar)</Label>
              <Input 
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePassword(false)} className="border-[#d0c4b0]">
              İptal
            </Button>
            <Button 
              onClick={handleChangePassword}
              disabled={passwordLoading}
              className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
              data-testid="change-password-btn"
            >
              {passwordLoading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
